// ============================================================================
// LITIGATION CASE HOOKS — Phase 9a
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { litigationCaseService } from '../../api/services/litigationCaseService';
import type {
  LitigationCase,
  LitigationCaseListItem,
  LitigationCaseSearchRequest,
} from '../../api/types/litigationCase.types';
import type { ID } from '../../api/types';

export const useLitigationCases = (params?: LitigationCaseSearchRequest) => {
  const [data, setData] = useState<LitigationCaseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await litigationCaseService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, pagination, refetch: fetch };
};

export const useClaimLitigationCase = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const claim = useCallback(async (
    id: ID,
    options?: { onSuccess?: (item: LitigationCase) => void; onError?: (error: Error) => void },
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await litigationCaseService.claim(id);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { claim, loading, error };
};

export const useLitigationCase = (id: ID | undefined) => {
  const [data, setData] = useState<LitigationCase | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const response = await litigationCaseService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
};
